import { useContext, useEffect, useState } from "react";
import { AuthContext } from "@modules/auth/contexts/auth-plus.context";
import { TrainersEntity } from "@modules/trainers/entities/trainers.entity";
import { useTrainers } from "@modules/trainers/hooks/use-trainers.hook";
import PaginationBar from "components/shared/Pagination/PaginationBar";
import { SearchInput } from "components/shared/SearchInput/SearchInput";
import { FilterBar } from "components/shared/FilterBar/FilterBar";
import { FilterFieldConfig } from "types/FilterFieldConfig";
import { message } from "antd";
import avatarDefault from "@assets/images/avatar-nobody.jpg";
import { useNavigate } from "react-router-dom";
import { FiEye } from "react-icons/fi";
import { getSubscriptionBadge } from "@modules/shared/helpers/subscription.helper";

export default function TrainerVerificationPage() {
  const { auth } = useContext(AuthContext);
  const { getTrainers } = useTrainers();
  const navigate = useNavigate();

  const [trainerList, setTrainerList] = useState<TrainersEntity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);

  // Filter states
  const [searchText, setSearchText] = useState("");
  const [verifiedFilter, setVerifiedFilter] = useState<string | undefined>(
    undefined,
  );
  const [sortFilter, setSortFilter] = useState<string>("newest");

  const fetchTrainers = async () => {
    if (!auth) return;
    setLoading(true);
    setError(null);

    try {
      const verified =
        verifiedFilter === "verified"
          ? true
          : verifiedFilter === "unverified"
            ? false
            : undefined;
      const sort =
        sortFilter === "newest" ? -1 : sortFilter === "oldest" ? 1 : undefined;

      const data = await getTrainers(page, verified, sort);
      setTrainerList(data?.TrainersList ?? []);
      setTotal(data?.Pagination?.getTotal() ?? 0);
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Failed to fetch trainers");
      message.error(err.message || "Failed to fetch trainers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrainers();
  }, [auth, page, verifiedFilter, sortFilter]);

  useEffect(() => {
    setPage(1);
  }, [verifiedFilter, sortFilter, searchText]);

  const totalPages = Math.ceil(total / limit);

  const formatAddress = (trainer: TrainersEntity) => {
    const address = trainer.address;
    if (!address) return "Address not available";

    const parts = [address.district, address.city, address.country]
      .filter(Boolean)
      .filter((value, index, self) => self.indexOf(value) === index);

    return parts.length > 0 ? parts.join(", ") : "Address not available";
  };

  const filterFields: FilterFieldConfig<{
    verified: string | undefined;
    sort: string | undefined;
  }>[] = [
    {
      key: "verified",
      label: "Verified",
      type: "select",
      options: [
        { value: "verified", label: "Verified" },
        { value: "unverified", label: "Unverified" },
      ],
    },
    {
      key: "sort",
      label: "Sort",
      type: "select",
      options: [
        { value: "newest", label: "Newest" },
        { value: "oldest", label: "Oldest" },
      ],
      hideAllOption: true,
    },
  ];

  return (
    <div className="mt-2">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-3">
        <SearchInput
          value={searchText}
          onChange={setSearchText}
          placeholder="Search trainers by name..."
          width="280px"
        />
        <FilterBar
          filters={{ verified: verifiedFilter, sort: sortFilter }}
          fields={filterFields}
          onChange={(key, value) => {
            if (key === "verified") setVerifiedFilter(value);
            if (key === "sort") {
              setSortFilter(value || "newest");
            }
          }}
          ignoreActiveKeys={["sort"]}
        />
      </div>

      {loading && <div className="mb-2 text-primary">Loading trainers...</div>}
      {error && <div className="mb-2 text-danger">{error}</div>}

      <div className="table-responsive shadow-sm border rounded">
        <table className="table table-hover align-middle">
          <thead className="table-light">
            <tr>
              <th>Name</th>
              <th>Subscription</th>
              <th>Expertise</th>
              <th>Address</th>
              <th>Verified</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {trainerList.map((trainer) => (
              <tr key={trainer.id}>
                <td>
                  <img
                    src={trainer.avatars?.[0]?.url ?? avatarDefault}
                    alt={trainer.name}
                    width={50}
                    height={50}
                    className="rounded-circle me-3"
                  />
                  {trainer.name}
                </td>
                <td>
                  {(() => {
                    const sub = getSubscriptionBadge(trainer.subscription);
                    return (
                      <span className={sub.className} style={sub.style}>
                        {sub.label}
                      </span>
                    );
                  })()}
                </td>
                <td>{trainer.expertise}</td>
                <td>{formatAddress(trainer)}</td>
                <td>
                  <span
                    className={`badge ${trainer.verified ? "bg-success" : "bg-danger"}`}
                  >
                    {trainer.verified ? "Verified" : "Unverified"}
                  </span>
                </td>
                <td>
                  <button
                    className="btn btn-icon btn-pills btn-soft-primary"
                    onClick={(e) => {
                      e.currentTarget.blur(); // ✅ Xóa focus ngay lập tức
                      navigate(`/trainers/${trainer.id}`);
                    }}
                  >
                    <FiEye />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3">
        <PaginationBar
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
          total={total}
        />
      </div>
    </div>
  );
}
